import type { FieldManifest } from '../../../core/fields/types';
import { FieldType } from '../../../core/fields/types';
import { orgchartBaseFieldPack } from './packs/base.pack';

/**
 * Person properties that can be changed as scalar facts through an Atlas
 * planning change-set. Position, reporting, and stable identity properties
 * have dedicated intent semantics and must never enter this generic lane.
 */
const STRUCTURAL_OR_IDENTITY_FIELDS = new Set([
  'title',
  'department',
  'reportsTo',
  'personId',
]);

const WRITABLE_FIELD_TYPES = new Set<FieldType>([
  FieldType.TEXT,
  FieldType.DATE,
  FieldType.SELECT,
]);

export const orgPersonWritableFields: readonly FieldManifest[] = orgchartBaseFieldPack.fields
  .filter((field) => !field.readonly)
  .filter((field) => WRITABLE_FIELD_TYPES.has(field.type))
  .filter((field) => !STRUCTURAL_OR_IDENTITY_FIELDS.has(field.id));

const writableFieldsById = new Map(orgPersonWritableFields.map((field) => [field.id, field]));

export function orgPersonWritableField(fieldId: string): FieldManifest | null {
  return writableFieldsById.get(fieldId) ?? null;
}

export function orgPersonFieldLabel(fieldId: string): string {
  return orgPersonWritableField(fieldId)?.label ?? fieldId.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export function normalizeOrgPersonFieldValue(value: string): string;
export function normalizeOrgPersonFieldValue(value: unknown): unknown;
export function normalizeOrgPersonFieldValue(value: unknown): unknown {
  return typeof value === 'string' ? value.trim() : value;
}

export function orgPersonFieldValidationError(fieldId: string, value: unknown): string | null {
  const field = orgPersonWritableField(fieldId);
  if (!field) return 'This person field is not writable through an organization plan.';
  if (typeof value !== 'string') return `${field.label} must be text.`;
  const normalized = value.trim();
  if (field.maxLength !== undefined && normalized.length > field.maxLength) {
    return `${field.label} must contain at most ${field.maxLength} characters.`;
  }
  if (field.minLength !== undefined && normalized.length < field.minLength) {
    return `${field.label} must contain at least ${field.minLength} characters.`;
  }
  if (field.type === FieldType.SELECT && !field.allowCustomOptions && field.options
    && !field.options.some((option) => option.value === normalized)) {
    return `${field.label} is not a registered option.`;
  }
  return null;
}
